/**
 * Minimal signature capture (design Tech Decision #8, no dependency): draws on a <canvas> with
 * mouse / touch / pen via Pointer Events and mirrors the drawing as a PNG data URL into a hidden input.
 *
 * Markup:
 *   <div class="signature-pad" data-input="#signature">
 *       <canvas width="480" height="180"></canvas>
 *       <button type="button" class="signature-clear">Hapus</button>
 *   </div>
 *   <input type="hidden" name="signature" id="signature">
 */
export class SignaturePad {
    constructor(root, { lineWidth = 2, color = '#111' } = {}) {
        this.root = typeof root === 'string' ? document.querySelector(root) : root;
        this.canvas = this.root.querySelector('canvas');
        this.input = document.querySelector(this.root.dataset.input || '');
        this.ctx = this.canvas.getContext('2d');
        this.lineWidth = lineWidth;
        this.color = color;
        this.drawing = false;
        this.dirty = false;
        this.last = null;
        this.bind();
        this.root.signaturePad = this;
    }

    bind() {
        const c = this.canvas;
        c.style.touchAction = 'none';
        c.addEventListener('pointerdown', (e) => this.start(e));
        c.addEventListener('pointermove', (e) => this.move(e));
        ['pointerup', 'pointerleave', 'pointercancel'].forEach((t) => c.addEventListener(t, () => this.end()));
        const clear = this.root.querySelector('.signature-clear');
        if (clear) clear.addEventListener('click', (e) => { e.preventDefault(); this.clear(); });
    }

    point(e) {
        const r = this.canvas.getBoundingClientRect();
        const sx = r.width ? this.canvas.width / r.width : 1;
        const sy = r.height ? this.canvas.height / r.height : 1;
        return { x: (e.clientX - r.left) * sx, y: (e.clientY - r.top) * sy };
    }

    start(e) {
        e.preventDefault();
        this.drawing = true;
        this.last = this.point(e);
        if (this.canvas.setPointerCapture && e.pointerId !== undefined) {
            try { this.canvas.setPointerCapture(e.pointerId); } catch (err) { /* synthetic events */ }
        }
        this.line(this.last, { x: this.last.x + 0.1, y: this.last.y + 0.1 });
    }

    move(e) {
        if (!this.drawing) return;
        e.preventDefault();
        const p = this.point(e);
        this.line(this.last, p);
        this.last = p;
    }

    end() {
        if (!this.drawing) return;
        this.drawing = false;
        this.sync();
    }

    line(a, b) {
        const ctx = this.ctx;
        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        this.dirty = true;
    }

    isEmpty() {
        return !this.dirty;
    }

    toDataURL() {
        return this.isEmpty() ? '' : this.canvas.toDataURL('image/png');
    }

    sync() {
        if (this.input) this.input.value = this.toDataURL();
    }

    clear() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.dirty = false;
        this.sync();
    }
}

/** Initialises every `.signature-pad` once. */
export function initSignaturePads(scope = document) {
    scope.querySelectorAll('.signature-pad').forEach((el) => {
        if (!el.signaturePad) new SignaturePad(el);
    });
}
